import { X } from '@phosphor-icons/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/cn';
import { IconButton } from '@/components/primitives/icon-button';
import { TooltipProvider } from '@/components/primitives/tooltip';

/** 復原提示的預設停留時間；夠久到能反應，又不會擋住工作。 */
export const TOAST_DURATION_MS = 7000;

export interface ToastAction {
  label: string;
  onAction: () => void | Promise<void>;
}

export interface ToastInput {
  message: string;
  detail?: string;
  tone?: 'default' | 'success' | 'danger';
  action?: ToastAction;
  durationMs?: number;
}

interface ToastRecord extends ToastInput {
  id: number;
}

interface ToastContextValue {
  show: (input: ToastInput) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONES = {
  default: 'border-border',
  success: 'border-success/40',
  danger: 'border-danger/40',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((input: ToastInput) => {
    nextId.current += 1;
    const record: ToastRecord = { ...input, id: nextId.current };
    // 一次只留最近三則，避免連續操作把畫面塞滿。
    setToasts((current) => [...current.slice(-2), record]);
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext value={value}>
      {children}
      <TooltipProvider delayDuration={280}>
        <output
          aria-label="操作提示"
          aria-live="polite"
          className="pointer-events-none fixed right-6 bottom-6 z-[70] flex w-[22rem] flex-col gap-2"
        >
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
        </output>
      </TooltipProvider>
    </ToastContext>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: (id: number) => void }) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), toast.durationMs ?? TOAST_DURATION_MS);
    return () => clearTimeout(timer);
  }, [toast, onDismiss]);

  return (
    <div
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-[var(--radius-panel)] border',
        'bg-surface-raised px-4 py-3 shadow-[var(--shadow-raised)]',
        'motion-safe:animate-[toast-in_280ms_var(--ease-gentle)]',
        TONES[toast.tone ?? 'default'],
      )}
    >
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-[0.875rem] leading-snug">{toast.message}</p>
        {toast.detail && (
          <p className="text-[0.75rem] leading-relaxed text-text-muted">{toast.detail}</p>
        )}
      </div>

      {toast.action && (
        <button
          type="button"
          onClick={() => {
            void toast.action?.onAction();
            onDismiss(toast.id);
          }}
          className="shrink-0 rounded-full px-3 py-1 text-[0.8125rem] font-medium text-accent hover:bg-accent-soft"
        >
          {toast.action.label}
        </button>
      )}

      <IconButton
        label="關閉提示"
        tooltipSide="left"
        icon={<X size={15} />}
        className="-mt-1 -mr-2 size-8"
        onClick={() => onDismiss(toast.id)}
      />
    </div>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast 必須放在 ToastProvider 之內');
  return context;
}
